import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Insights as InsightsIcon,
  Refresh as RefreshIcon,
  WarningAmber as WarningIcon,
  CheckCircleOutline as OkIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { CATEGORICAL_DARK, CATEGORICAL_LIGHT, NEUTRAL_DARK, NEUTRAL_LIGHT } from '../../utils/chartPalette';

interface Option {
  id: string;
  name: string;
}

interface TimePeriod extends Option {
  startDate: string;
}

// See api/DTOs/ValidationDto.cs (ValidationDashboardDto) and
// api/Services/Validation/ValidationDashboardService.cs for how each
// number is worked out.
interface RegionRow {
  region: string;
  plantedHa: number;
  defoliatedHa: number;
  plantedHaByIrrigation: Record<string, number>;
  plantedHaByGeneStack: Record<string, number>;
}

interface Shortfall {
  businessName: string;
  farmName?: string;
  fieldName?: string;
  region: string;
  irrigation: string;
  plantedHa: number;
  defoliatedHa: number;
}

interface Dashboard {
  regions: RegionRow[];
  defoliationShortfalls: Shortfall[];
  irrigationTypes: string[];
  geneStacks: string[];
  plantingCount: number;
  plantingsWithoutArea: number;
  plantingsWithoutRegion: number;
  plantingsWithoutIrrigation: number;
  plantingsWithoutGeneStack: number;
  defoliatedFieldCount: number;
  defoliatedWithoutPlanting: number;
  recordsWithoutRegion: number;
}

// The service's names for its gap buckets.
const NO_REGION = 'No region';
const NO_GENE_STACK = 'No gene stack';
const UNKNOWN_IRRIGATION = 'Unknown';

// Every region labelled, at 45° so long or many names don't overlap.
const regionAxis = { dataKey: 'region', interval: 0, angle: -45, textAnchor: 'end', height: 90, tick: { fontSize: 12 } } as const;

// Legend labels in text colour; the swatch alone carries the series.
const legendText = (value: string) => <span style={{ color: 'var(--legend-text)' }}>{value}</span>;

const formatHa = (value: number) => `${Math.round(value).toLocaleString('en-AU')} ha`;
const formatPercent = (part: number, whole: number) => (whole > 0 ? `${Math.round((100 * part) / whole)}%` : '-');

interface Series {
  key: string;
  color: string;
}

const ChartCard: React.FC<{ title: string; note?: string; children: React.ReactNode }> = ({ title, note, children }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: '1 1 440px', minWidth: 0 }}>
    <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
    {note && <Typography variant="caption" color="text.secondary" display="block">{note}</Typography>}
    <Box sx={{ mt: 1 }}>{children}</Box>
  </Paper>
);

const StatTile: React.FC<{ label: string; value: string; detail?: string }> = ({ label, value, detail }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: '1 1 180px', minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 600 }}>{value}</Typography>
    {detail && <Typography variant="body2" color="text.secondary">{detail}</Typography>}
  </Paper>
);

// Shared hover card: label, then each series' hectares and share of the
// row's total, in text colour beside a colour swatch.
const HaTooltip: React.FC<any> = ({ active, payload, label, showShare }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((sum: number, p: any) => sum + Number(p.value || 0), 0);
  return (
    <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      {label && <Typography variant="body2" fontWeight={600}>{label}</Typography>}
      {payload.filter((p: any) => Number(p.value) > 0).map((p: any) => (
        <Box key={p.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: p.color || p.payload?.fill }} />
          <Typography variant="body2">
            {p.name}: {formatHa(Number(p.value))}
            {showShare && payload.length > 1 && ` (${formatPercent(Number(p.value), total)})`}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// A region-by-series stacked bar (one bar per region) beside a donut of
// the same series' totals. The donut only shows part-to-whole; the bar
// carries the comparison between regions.
const StackedWithDonut: React.FC<{
  rows: RegionRow[];
  series: Series[];
  valueOf: (row: RegionRow, key: string) => number;
  surface: string;
  gridColor: string;
}> = ({ rows, series, valueOf, surface, gridColor }) => {
  const shown = series.filter((s) => rows.some((r) => valueOf(r, s.key) > 0));
  const data = rows.map((r) => ({ region: r.region, ...Object.fromEntries(shown.map((s) => [s.key, valueOf(r, s.key)])) }));
  const totals = shown.map((s) => ({ name: s.key, value: rows.reduce((sum, r) => sum + valueOf(r, s.key), 0), color: s.color }));
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
      <Box sx={{ flex: '2 1 320px', minWidth: 0 }}>
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            <XAxis {...regionAxis} />
            <YAxis tickFormatter={(v) => Number(v).toLocaleString('en-AU')} width={60} />
            <Tooltip content={<HaTooltip showShare />} cursor={{ fill: gridColor, opacity: 0.3 }} />
            <Legend formatter={legendText} />
            {shown.map((s) => (
              <Bar key={s.key} dataKey={s.key} name={s.key} stackId="ha" fill={s.color} stroke={surface} strokeWidth={1} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie data={totals} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" stroke={surface} strokeWidth={2} isAnimationActive={false}>
              {totals.map((t) => <Cell key={t.name} fill={t.color} />)}
            </Pie>
            <Tooltip content={<HaTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {totals.map((t) => (
            <Box key={t.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: t.color, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={t.name}>{t.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatPercent(t.value, totals.reduce((sum, x) => sum + x.value, 0))}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// Admin → Validation → Dashboard: the numbers worth eyeballing after an
// import — planted hectares by region, split by Irrigated/Dryland and by
// gene stack, against defoliated hectares. Gaps (no region, no gene stack,
// unknown irrigation) are their own grey buckets so they stand out; the
// Data Validation screen lists the records behind them.
const ValidationDashboard: React.FC = () => {
  const auth = useAuth();
  const theme = useTheme();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);
  const [businesses, setBusinesses] = useState<Option[]>([]);
  const [timePeriods, setTimePeriods] = useState<TimePeriod[]>([]);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [timePeriodId, setTimePeriodId] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dark = theme.palette.mode === 'dark';
  const categorical = dark ? CATEGORICAL_DARK : CATEGORICAL_LIGHT;
  const neutral = dark ? NEUTRAL_DARK : NEUTRAL_LIGHT;
  const surface = theme.palette.background.paper;
  const gridColor = theme.palette.divider;

  useEffect(() => {
    if (!auth.isSignedIn || !auth.tokens?.accessToken) return;
    (async () => {
      const [businessRes, tpRes] = await Promise.all([
        fetch('/api/businesses/list', { headers: authHeaders() }),
        fetch('/api/time-periods', { headers: authHeaders() }),
      ]);
      if (businessRes.ok) setBusinesses((await businessRes.json()).map((b: any) => ({ id: b.id, name: b.name })));
      if (tpRes.ok) {
        const periods: TimePeriod[] = (await tpRes.json()).map((t: any) => ({ id: t.id, name: t.name, startDate: t.startDate }));
        periods.sort((a, b) => b.startDate.localeCompare(a.startDate));
        setTimePeriods(periods);
        // The latest season that has started — mixing seasons would add
        // up the same field's hectares more than once.
        const today = new Date().toISOString().slice(0, 10);
        setTimePeriodId((periods.find((p) => p.startDate <= today) ?? periods[0])?.id ?? '');
      } else {
        setTimePeriodId('');
      }
    })();
  }, [auth.isSignedIn, auth.tokens?.accessToken, authHeaders]);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (businessId) p.set('businessId', businessId);
    if (timePeriodId) p.set('timePeriodId', timePeriodId);
    return p.toString();
  }, [businessId, timePeriodId]);

  const fetchDashboard = useCallback(async () => {
    if (timePeriodId === null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/validation/dashboard?${params()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load the dashboard');
      setDashboard(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [timePeriodId, params, authHeaders]);

  // The shortfall list as an .xlsx, for the same business and season.
  const handleExportShortfalls = async () => {
    setDownloading(true);
    setError(null);
    try {
      const res = await fetch(`/api/validation/dashboard/defoliation-shortfalls?${params()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Export failed');
      const disposition = res.headers.get('Content-Disposition') || '';
      const fileName = /filename="?([^";]+)"?/.exec(disposition)?.[1] || 'defoliation_shortfalls.xlsx';
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Colour follows the entity: the server lists every irrigation type and
  // gene stack in name order whatever the filter, and the gap buckets are
  // neutral grey.
  const irrigationSeries: Series[] = useMemo(() => {
    if (!dashboard) return [];
    const extra = Array.from(new Set(dashboard.regions.flatMap((r) => Object.keys(r.plantedHaByIrrigation))))
      .filter((k) => k !== UNKNOWN_IRRIGATION && !dashboard.irrigationTypes.includes(k));
    // Irrigated first, so it takes the first (blue) hue.
    const ordered = [...dashboard.irrigationTypes].sort((a, b) => Number(b === 'Irrigated') - Number(a === 'Irrigated'));
    return [
      ...[...ordered, ...extra].map((key, i) => ({ key, color: categorical[i] ?? neutral })),
      { key: UNKNOWN_IRRIGATION, color: neutral },
    ];
  }, [dashboard, categorical, neutral]);

  const geneStackSeries: Series[] = useMemo(() => {
    if (!dashboard) return [];
    return [
      ...dashboard.geneStacks.map((key, i) => ({ key, color: categorical[i] ?? neutral })),
      { key: NO_GENE_STACK, color: neutral },
    ];
  }, [dashboard, categorical, neutral]);

  const regions = dashboard?.regions ?? [];
  const totalPlanted = regions.reduce((sum, r) => sum + r.plantedHa, 0);
  const totalDefoliated = regions.reduce((sum, r) => sum + r.defoliatedHa, 0);
  const irrigationTotal = (key: string) => regions.reduce((sum, r) => sum + (r.plantedHaByIrrigation[key] ?? 0), 0);
  const gaps = dashboard
    ? [
        { label: 'records with no region', count: dashboard.recordsWithoutRegion },
        { label: 'plantings with no area', count: dashboard.plantingsWithoutArea },
        { label: 'plantings with no Irrigated/Dryland', count: dashboard.plantingsWithoutIrrigation },
        { label: 'plantings with no gene stack', count: dashboard.plantingsWithoutGeneStack },
        { label: 'defoliated fields with no planting', count: dashboard.defoliatedWithoutPlanting },
      ]
    : [];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5} mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <InsightsIcon />
          Validation Dashboard
        </Typography>
        <IconButton onClick={fetchDashboard} title="Refresh"><RefreshIcon /></IconButton>
      </Box>

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5} mb={2}>
        <Autocomplete
          size="small"
          sx={{ minWidth: 280, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
          options={businesses}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={businesses.find((b) => b.id === businessId) || null}
          onChange={(_, value) => setBusinessId(value?.id || null)}
          renderInput={(params) => <TextField {...params} label="Business (and its children)" placeholder="All businesses" />}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="validation-dashboard-season-label">Season</InputLabel>
          <Select
            labelId="validation-dashboard-season-label"
            label="Season"
            value={timePeriodId ?? ''}
            onChange={(e) => setTimePeriodId(e.target.value)}
          >
            <MenuItem value=""><em>All seasons</em></MenuItem>
            {timePeriods.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {timePeriodId === '' && (
        <Alert severity="info" sx={{ mb: 2 }}>All seasons are added together, so a field planted in two seasons counts twice.</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && !dashboard ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : dashboard && regions.length === 0 ? (
        <Alert severity="info">No plantings or defoliation records for this selection.</Alert>
      ) : dashboard && (
        <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s', '--legend-text': theme.palette.text.primary }}>
          <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
            <StatTile label="Planted" value={formatHa(totalPlanted)} detail={`${dashboard.plantingCount.toLocaleString('en-AU')} plantings`} />
            {irrigationSeries.filter((s) => s.key !== UNKNOWN_IRRIGATION && irrigationTotal(s.key) > 0).map(({ key: name }) => (
              <StatTile key={name} label={name} value={formatHa(irrigationTotal(name))} detail={`${formatPercent(irrigationTotal(name), totalPlanted)} of planted`} />
            ))}
            <StatTile label="Defoliated" value={formatHa(totalDefoliated)} detail={`${formatPercent(totalDefoliated, totalPlanted)} of planted`} />
            <Paper variant="outlined" sx={{ p: 2, flex: '1 1 240px', minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">Gaps to check</Typography>
              {gaps.map((gap) => (
                <Box key={gap.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {gap.count > 0
                    ? <WarningIcon fontSize="small" color="warning" />
                    : <OkIcon fontSize="small" color="success" />}
                  <Typography variant="body2">{gap.count.toLocaleString('en-AU')} {gap.label}</Typography>
                </Box>
              ))}
            </Paper>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
            <ChartCard title="Planted ha by region">
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={regions} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                  <XAxis {...regionAxis} />
                  <YAxis tickFormatter={(v) => Number(v).toLocaleString('en-AU')} width={60} />
                  <Tooltip content={<HaTooltip />} cursor={{ fill: gridColor, opacity: 0.3 }} />
                  <Bar dataKey="plantedHa" name="Planted" radius={[4, 4, 0, 0]}>
                    {regions.map((r) => <Cell key={r.region} fill={r.region === NO_REGION ? neutral : categorical[0]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Planted vs defoliated ha by region"
              note="Defoliated: each field's defoliant / harvest aid records added up, capped at what the field had planted — so passes split to match staggered plantings add up, and a second shot isn't counted twice."
            >
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={regions} margin={{ top: 5, right: 10, left: 10, bottom: 5 }} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                  <XAxis {...regionAxis} />
                  <YAxis tickFormatter={(v) => Number(v).toLocaleString('en-AU')} width={60} />
                  <Tooltip
                    cursor={{ fill: gridColor, opacity: 0.3 }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const row = payload[0].payload as RegionRow;
                      return (
                        <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{label}</Typography>
                          <Typography variant="body2">Planted: {formatHa(row.plantedHa)}</Typography>
                          <Typography variant="body2">Defoliated: {formatHa(row.defoliatedHa)} ({formatPercent(row.defoliatedHa, row.plantedHa)} of planted)</Typography>
                        </Box>
                      );
                    }}
                  />
                  <Legend formatter={legendText} />
                  <Bar dataKey="plantedHa" name="Planted" fill={categorical[0]} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="defoliatedHa" name="Defoliated" fill={categorical[1]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>

          <Box display="flex" flexDirection="column" gap={2} mb={2}>
            <ChartCard title="Irrigated and dryland planted ha by region">
              <StackedWithDonut
                rows={regions}
                series={irrigationSeries}
                valueOf={(r, key) => r.plantedHaByIrrigation[key] ?? 0}
                surface={surface}
                gridColor={gridColor}
              />
            </ChartCard>
            <ChartCard title="Gene stack planted ha by region">
              <StackedWithDonut
                rows={regions}
                series={geneStackSeries}
                valueOf={(r, key) => r.plantedHaByGeneStack[key] ?? 0}
                surface={surface}
                gridColor={gridColor}
              />
            </ChartCard>
          </Box>

          <Paper variant="outlined" sx={{ mb: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Region</TableCell>
                    <TableCell align="right">Planted</TableCell>
                    {irrigationSeries.filter((s) => regions.some((r) => (r.plantedHaByIrrigation[s.key] ?? 0) > 0)).map((s) => (
                      <TableCell key={s.key} align="right">{s.key}</TableCell>
                    ))}
                    <TableCell align="right">Defoliated</TableCell>
                    <TableCell align="right">Defoliated %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {regions.map((r) => (
                    <TableRow key={r.region}>
                      <TableCell>{r.region}</TableCell>
                      <TableCell align="right">{formatHa(r.plantedHa)}</TableCell>
                      {irrigationSeries.filter((s) => regions.some((x) => (x.plantedHaByIrrigation[s.key] ?? 0) > 0)).map((s) => (
                        <TableCell key={s.key} align="right">{formatHa(r.plantedHaByIrrigation[s.key] ?? 0)}</TableCell>
                      ))}
                      <TableCell align="right">{formatHa(r.defoliatedHa)}</TableCell>
                      <TableCell align="right">{formatPercent(r.defoliatedHa, r.plantedHa)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined">
            <Box sx={{ p: 2, pb: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Fields with less defoliated than planted ({dashboard.defoliationShortfalls.length.toLocaleString('en-AU')})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  A missing defoliation record, or a field not defoliated yet. Biggest shortfall first.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                disabled={downloading || dashboard.defoliationShortfalls.length === 0}
                onClick={handleExportShortfalls}
              >
                Export to Excel
              </Button>
            </Box>
            {dashboard.defoliationShortfalls.length === 0 ? (
              <Typography variant="body2" sx={{ px: 2, pb: 2 }}>Every planted field is fully defoliated.</Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Business</TableCell>
                      <TableCell>Farm</TableCell>
                      <TableCell>Field</TableCell>
                      <TableCell>Region</TableCell>
                      <TableCell>Irrigation</TableCell>
                      <TableCell align="right">Planted</TableCell>
                      <TableCell align="right">Defoliated</TableCell>
                      <TableCell align="right">Short by</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboard.defoliationShortfalls.map((f, i) => (
                      <TableRow key={i}>
                        <TableCell>{f.businessName}</TableCell>
                        <TableCell>{f.farmName || '-'}</TableCell>
                        <TableCell>{f.fieldName || '-'}</TableCell>
                        <TableCell>{f.region}</TableCell>
                        <TableCell>{f.irrigation}</TableCell>
                        <TableCell align="right">{formatHa(f.plantedHa)}</TableCell>
                        <TableCell align="right">{formatHa(f.defoliatedHa)}</TableCell>
                        <TableCell align="right">{formatHa(f.plantedHa - f.defoliatedHa)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ValidationDashboard;
